import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Autocomplete, TextField, Tooltip } from '@mui/material';

const STATE_ABBR = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR',
  california: 'CA', colorado: 'CO', connecticut: 'CT', delaware: 'DE',
  florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID',
  illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
  kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT',
  vermont: 'VT', virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
  wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC',
  'ontario': 'ON', 'quebec': 'QC', 'british columbia': 'BC', 'alberta': 'AB',
  'manitoba': 'MB', 'saskatchewan': 'SK', 'nova scotia': 'NS', 'new brunswick': 'NB'
};

const AddressAutocomplete = forwardRef(
  (
    {
      inputRef,
      disabled,
      locationFlow,
      onInputChange,
      onSelectDetails,
      defaultValue = '',
      inputVariant = 'outlined',
      label = 'Address',
      validattionErrors,
      errorMsg,
      inputType
    },
    ref
  ) => {
    const [options, setOptions] = useState([]);
    const [inputValueApi, setInputValueApi] = useState(defaultValue);
    const [inputValue, setInputValue] = useState(defaultValue);
    const [highlightedOption, setHighlightedOption] = useState(null);
    const [addressError, setAddressError] = useState('');
    const [userOriginalInput, setUserOriginalInput] = useState('');
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [lastSelectedType, setLastSelectedType] = useState(null); // 'manual' or 'google'
    const autocompleteService = useRef(null);

    // --- INITIALIZE DEFAULT VALUE ---
    useEffect(() => {
      setInputValue(defaultValue);
    }, [defaultValue]);

    // --- SETUP GOOGLE AUTOCOMPLETE SERVICE ---
    useEffect(() => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
    }, []);

    // --- PARSE USER TYPED ADDRESS ---
    const parseUserTypedAddress = address => {
      const result = {
        company: '',
        streetLines: address,
        city: '',
        state: '',
        zip: '',
        country: 'USA',
        isUserTyped: true
      };

      try {
        // Try comma-separated format: "CITY, STATE ZIP" or "CITY, STATE ZIP, COUNTRY"
        // Handles both "PA 19611" and "PA, 19611" (comma between state and zip)
        const cityStateZipRegex =
          /([A-Za-z\s]+),\s*([A-Z]{2}),?\s*(\d{5}(?:-\d{4})?)\s*,?\s*([A-Za-z]*)$/;
        const match = address.match(cityStateZipRegex);

        if (match) {
          result.city = match[1].trim();
          result.state = match[2].trim();
          result.zip = match[3].trim();
          if (match[4]) result.country = match[4].trim();
          result.streetLines = address
            .replace(cityStateZipRegex, '')
            .replace(/,\s*$/, '')
            .trim();
        } else {
          // Try space-separated format: "Company StreetNum StreetName City STATE ZIP Country"
          // e.g. "Brown & Partners Inc. 2131 Adelaide Street Toronto TX 76240 USA"
          const stateZipRegex =
            /\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\s*(USA|US|Canada|CA|Mexico|MX)?\s*$/;
          const stateZipMatch = address.match(stateZipRegex);

          if (stateZipMatch) {
            result.state = stateZipMatch[1];
            result.zip = stateZipMatch[2];
            if (stateZipMatch[3]) result.country = stateZipMatch[3];

            const remaining = address.slice(0, stateZipMatch.index).trim();

            // Split company from street by finding the first street number
            const streetNumIdx = remaining.search(/\b\d+\s+[A-Za-z]/);
            const companyPart =
              streetNumIdx > 0 ? remaining.slice(0, streetNumIdx).trim() : '';
            const streetAndCity =
              streetNumIdx !== -1 ? remaining.slice(streetNumIdx) : remaining;

            // Split street from city using common street type suffixes
            const streetTypePattern =
              /\b(Street|St\.?|Avenue|Ave\.?|Boulevard|Blvd\.?|Drive|Dr\.?|Road|Rd\.?|Lane|Ln\.?|Way|Court|Ct\.?|Place|Pl\.?|Circle|Cir\.?|Highway|Hwy\.?|Parkway|Pkwy\.?)\b/gi;
            const streetMatches = [...streetAndCity.matchAll(streetTypePattern)];

            if (streetMatches.length > 0) {
              const lastSuffix = streetMatches[streetMatches.length - 1];
              const splitPoint = lastSuffix.index + lastSuffix[0].length;
              result.streetLines = streetAndCity.slice(0, splitPoint).trim();
              result.city = streetAndCity.slice(splitPoint).trim();
            } else {
              result.streetLines = streetAndCity;
            }

            if (companyPart) result.company = companyPart;
          } else {
            // Try full state name format: "Company Street City StateName ZIP Country"
            // e.g. "AB Tech Solution 67 Tyngboro Westford Massachusetts 01886 USA"
            const zipCountryRegex =
              /\b(\d{5}(?:-\d{4})?)\s*(USA|US|Canada|CA|Mexico|MX)?\s*$/;
            const zipMatch = address.match(zipCountryRegex);

            if (zipMatch) {
              result.zip = zipMatch[1];
              if (zipMatch[2]) result.country = zipMatch[2];

              const beforeZip = address.slice(0, zipMatch.index).trim();

              // Sort state names longest-first so "West Virginia" matches before "Virginia"
              const stateNames = Object.keys(STATE_ABBR).sort((a, b) => b.length - a.length);
              let stateFound = false;

              for (const stateName of stateNames) {
                const stateRegex = new RegExp(`\\b${stateName}\\s*$`, 'i');
                if (stateRegex.test(beforeZip)) {
                  result.state = STATE_ABBR[stateName];
                  const beforeState = beforeZip.replace(stateRegex, '').trim();

                  const snIdx = beforeState.search(/\b\d+\s+[A-Za-z]/);
                  const company = snIdx > 0 ? beforeState.slice(0, snIdx).trim() : '';
                  const streetAndCity2 = snIdx !== -1 ? beforeState.slice(snIdx) : beforeState;

                  const stPat =
                    /\b(Street|St\.?|Avenue|Ave\.?|Boulevard|Blvd\.?|Drive|Dr\.?|Road|Rd\.?|Lane|Ln\.?|Way|Court|Ct\.?|Place|Pl\.?|Circle|Cir\.?|Highway|Hwy\.?|Parkway|Pkwy\.?)\b/gi;
                  const stMatches = [...streetAndCity2.matchAll(stPat)];

                  if (stMatches.length > 0) {
                    const last = stMatches[stMatches.length - 1];
                    const sp = last.index + last[0].length;
                    result.streetLines = streetAndCity2.slice(0, sp).trim();
                    result.city = streetAndCity2.slice(sp).trim();
                  } else {
                    // No street type suffix — last word is city
                    const words = streetAndCity2.trim().split(/\s+/);
                    result.city = words.length > 1 ? words[words.length - 1] : '';
                    result.streetLines = words.length > 1 ? words.slice(0, -1).join(' ') : streetAndCity2;
                  }

                  if (company) result.company = company;
                  stateFound = true;
                  break;
                }
              }

              if (!stateFound) result.streetLines = beforeZip;
            }
          }
        }

        // For quickQuote with comma format: extract company from beginning of streetLines
        if (inputType === 'quickQuote' && result.streetLines && !result.company) {
          const parts = result.streetLines.split(/\s+(?=\d)/);
          if (parts.length > 1 && /^[A-Za-z\s&.,'-]+$/.test(parts[0].trim())) {
            result.company = parts[0].trim();
            result.streetLines = parts.slice(1).join(' ').trim();
          }
        }
      } catch (error) {
        console.log('Error parsing user address:', error);
        result.streetLines = address;
      }

      return result;
    };

    // --- CREATE CUSTOM SUGGESTION FOR DROPDOWN ---
    const createCustomSuggestion = address => {
      const parsed = parseUserTypedAddress(address);

      return {
        place_id: 'custom_user_typed',
        description: address,
        structured_formatting: {
          main_text:
            parsed.company ||
            parsed.streetLines.split(',')[0] ||
            parsed.streetLines,
          secondary_text: parsed.city
            ? `${parsed.city}, ${parsed.state} ${parsed.zip}`
            : 'Use manually entered address'
        },
        terms: [
          { value: parsed.company || '' },
          { value: parsed.streetLines || '' },
          { value: parsed.city || '' },
          { value: parsed.state || '' },
          { value: parsed.zip || '' }
        ].filter(term => term.value),
        isCustomSuggestion: true,
        parsedAddress: parsed,
        userOriginalInput: address
      };
    };

    // --- HANDLE USER TYPED ADDRESS (when no Google match found) ---
    const handleUserTypedAddress = address => {
      if (!address.trim()) return;

      const parsedAddress = parseUserTypedAddress(address);

      const addressDetails = {
        city: parsedAddress.city || '',
        state: parsedAddress.state || '',
        zip: parsedAddress.zip || '',
        country: parsedAddress.country || 'USA',
        streetLines: parsedAddress.streetLines || address,
        hasStreetLine: Boolean(parsedAddress.streetLines),
        residential: false,
        company: parsedAddress.company || '',
        isUserTyped: true
      };

      setInputValue(address);
      setInputValueApi(address);
      setAddressError('');
      setLastSelectedType('manual');

      console.log('🔍 Manual address processed:', addressDetails);
      onSelectDetails?.(addressDetails);
    };

    // --- CHECK IF ADDRESS LOOKS COMPLETE ---
    const isCompleteAddress = address => {
      if (!address || address.length < 10) return false;

      const hasCompleteAddressFormat = /[A-Za-z\s]+,\s*[A-Z]{2}\s*\d{5}/.test(address);
      const hasSpaceSeparatedFormat = /\b[A-Z]{2}\s+\d{5}\b/.test(address);
      const hasMultipleComponents = address.split(',').length >= 2;
      const hasZipCode = /\b\d{5}(?:-\d{4})?\b/.test(address);

      return hasCompleteAddressFormat || hasSpaceSeparatedFormat || hasMultipleComponents || hasZipCode;
    };

    // --- FETCH AUTOCOMPLETE PREDICTIONS WITH CUSTOM SUGGESTIONS ---
    useEffect(() => {
      if (!autocompleteService.current || !inputValue) {
        setOptions([]);
        return;
      }

      const cleanedInput = inputValue.replace(/[^a-zA-Z0-9\s,&.'-]/g, '');
      setUserOriginalInput(inputValue);

      if (cleanedInput) {
        autocompleteService.current.getPlacePredictions(
          {
            input: cleanedInput,
            componentRestrictions: { country: ['us', 'ca', 'mx'] }
          },
          predictions => {
            const sanitized = (predictions || []).map(p => ({
              ...p,
              description: inputValue.includes(',')
                ? p.description
                : p.description.replace(/,/g, ''),
              isGoogleSuggestion: true
            }));

            let finalOptions = [...sanitized];

            // Always append the manual suggestion at the bottom when address looks complete
            if (isCompleteAddress(inputValue)) {
              finalOptions = [...sanitized, createCustomSuggestion(inputValue)];
            }

            setOptions(finalOptions);
          }
        );
      } else {
        setOptions([]);
      }
    }, [inputValue]);

    // --- CUSTOM RENDER FOR DROPDOWN OPTIONS ---
    const renderOption = (props, option) => {
      const { key, ...otherProps } = props;

      if (option.isCustomSuggestion) {
        const displayText = option.userOriginalInput || option.description;
        // Split the address into logical parts for better display
        const addressParts = displayText
          .split(',')
          .map(part => part.trim())
          .filter(part => part);

        return (
          <li
            {...otherProps}
            key={key}
            style={{
              backgroundColor: '#f0f8ff',
              borderLeft: '4px solid #1890ff',
              padding: '8px 12px',
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <Tooltip
              arrow
              placement='top'
              title='Use your manually entered address'
            >
              <div style={{ width: '100%' }}>
                {/* Main company/street line - ensure it wraps properly */}
                <div
                  style={{
                    color: '#1890ff',
                    fontSize: '14px',
                    lineHeight: '1.3',
                    marginBottom: '2px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {addressParts[0] || displayText}
                </div>

                {/* Address details - show remaining parts */}
                {addressParts.length > 1 && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      lineHeight: '1.3',
                      marginBottom: '4px',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {addressParts.slice(1).join(', ')}
                  </div>
                )}
              </div>
            </Tooltip>
          </li>
        );
      }

      const mainText =
        option.structured_formatting?.main_text ||
        (option.description ? option.description.split(',')[0] : '');
      const secondaryText =
        option.structured_formatting?.secondary_text ||
        (option.description
          ? option.description.split(',').slice(1).join(',').trim()
          : '');

      return (
        <li
          {...otherProps}
          key={key}
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Tooltip arrow placement='top' title='Google Maps suggestion'>
            <div style={{ width: '100%' }}>
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.3',
                  marginBottom: '2px',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {mainText}
              </div>

              {secondaryText && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    lineHeight: '1.3',
                    marginBottom: '4px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}
                >
                  {secondaryText}
                </div>
              )}
            </div>
          </Tooltip>
        </li>
      );
    };

    // --- CLEAN ADDRESS COMPONENTS ---
    const cleanAddressComponents = components => {
      const extract = (type, useShort = false) =>
        components.find(c => c.types.includes(type))?.[
          useShort ? 'short_name' : 'long_name'
        ] || '';

      const streetNumber = extract('street_number');
      const route = extract('route');

      // Clean street lines - avoid duplication
      let streetLines = '';
      if (streetNumber && route) {
        streetLines = `${streetNumber} ${route}`.trim();
      } else if (route) {
        streetLines = route;
      } else if (streetNumber) {
        streetLines = streetNumber;
      }

      let countryCode = extract('country', true);
      if (countryCode === 'US') countryCode = 'USA';

      return {
        streetNumber,
        route,
        streetLines,
        city: extract('locality') || extract('sublocality') || '',
        state: extract('administrative_area_level_1', true),
        zip: extract('postal_code'),
        country: countryCode
      };
    };

    const getPredictionCompany = option => {
      if (!option) return '';
      return (
        option?.structured_formatting?.main_text ||
        option?.terms?.[0]?.value ||
        option?.description?.split(',')[0] ||
        ''
      ).trim();
    };

    const getPredictionDisplayValue = (company, fullAddress) => {
      if (!company) return fullAddress;
      const normalizedCompany = company.trim().toLowerCase();
      const normalizedAddress = fullAddress.trim().toLowerCase();
      return normalizedAddress.startsWith(normalizedCompany)
        ? fullAddress
        : `${company}, ${fullAddress}`;
    };

    const handleSelect = option => {
      if (
        !option?.place_id ||
        option.place_id === 'custom_user_typed' ||
        option.isCustomSuggestion
      ) {
        // Custom suggestion selected - use user typed address
        handleUserTypedAddress(option?.userOriginalInput || inputValue);
        return;
      }

      // Use PlacesService for quickQuote to get business names
      if (
        inputType === 'quickQuote' &&
        window.google?.maps?.places?.PlacesService
      ) {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );

        service.getDetails(
          {
            placeId: option.place_id,
            fields: ['name', 'address_components', 'formatted_address']
          },
          (place, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place
            ) {
              const components = place.address_components || [];
              const cleaned = cleanAddressComponents(components);

              const addressDetails = {
                city: cleaned.city,
                state: cleaned.state,
                zip: cleaned.zip,
                country: cleaned.country,
                streetLines: cleaned.streetLines,
                hasStreetLine: Boolean(cleaned.streetLines),
                residential: false,
                isUserTyped: false,
                company: '' // Reset company when selecting from Google
              };

              // Only set company if it's a valid business name and not duplicating street address.
              // Allow parentheses and digits in place names like "Stephens Pipe & Steel, LLC (Montoursville PA Office)".
              const companyCandidate = getPredictionCompany(option);
              if (place.name) {
                const placeName = place.name.trim();
                // Accept any non-empty name that doesn't look like a street address
              // (street addresses start with a house number, company names don't)
              const isProbablyCompany = placeName.length > 0 && !/^\d+\s/.test(placeName) && !/^\d+$/.test(placeName);
                const isDuplicateOfStreet =
                  placeName.toLowerCase() ===
                  cleaned.streetLines.toLowerCase();

                if (isProbablyCompany && !isDuplicateOfStreet) {
                  addressDetails.company = placeName;
                }
              }

              const isCompanyCandidateNumeric = /^\d{3,}(?:-\d{4})?$/.test(companyCandidate?.trim());
              if (
                !addressDetails.company &&
                companyCandidate &&
                !isCompanyCandidateNumeric &&
                companyCandidate.toLowerCase() !== cleaned.streetLines.toLowerCase()
              ) {
                addressDetails.company = companyCandidate;
              }

              const fullAddress =
                place.formatted_address || option.description || '';
              const streetOnly = fullAddress.split(',')[0];
              const finalValue =
                locationFlow === 'LTL' && cleaned.hasStreetLine
                  ? streetOnly
                  : fullAddress;

              const displayValue = addressDetails.company
                ? `${addressDetails.company}, ${fullAddress}`
                : fullAddress;

              setInputValue(displayValue);
              setInputValueApi(finalValue);
              setAddressError('');
              setLastSelectedType('google');

              console.log('🔍 Google address processed:', addressDetails);
              onSelectDetails?.(addressDetails);
            } else {
              const companyCandidate = getPredictionCompany(option);
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ placeId: option.place_id }, (results, status) => {
                if (status === 'OK' && results[0]) {
                  const components = results[0].address_components;
                  const cleaned = cleanAddressComponents(components);

                  const addressDetails = {
                    city: cleaned.city,
                    state: cleaned.state,
                    zip: cleaned.zip,
                    country: cleaned.country,
                    streetLines: cleaned.streetLines,
                    hasStreetLine: Boolean(cleaned.streetLines),
                    residential: false,
                    isUserTyped: false,
                    company: companyCandidate
                  };

                  const fullAddress = results[0].formatted_address;
                  const streetOnly = fullAddress.split(',')[0];
                  const finalValue =
                    locationFlow === 'LTL' && cleaned.hasStreetLine
                      ? streetOnly
                      : fullAddress;
                  const displayValue = getPredictionDisplayValue(
                    addressDetails.company,
                    fullAddress
                  );

                  setInputValue(displayValue);
                  setInputValueApi(finalValue);
                  setAddressError('');
                  setLastSelectedType('google');

                  console.log('🔍 Geocoder fallback address processed:',
                    addressDetails
                  );
                  onSelectDetails?.(addressDetails);
                  setHighlightedOption(null);
                } else {
                  handleUserTypedAddress(inputValue);
                }
              });
            }
          }
        );
        return;
      }

      // Fallback: geocoder for non-quickQuote or if PlacesService fails
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: option.place_id }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const components = results[0].address_components;
          const cleaned = cleanAddressComponents(components);

          const companyCandidate =
            inputType === 'quickQuote' ? getPredictionCompany(option) : '';
          const addressDetails = {
            city: cleaned.city,
            state: cleaned.state,
            zip: cleaned.zip,
            country: cleaned.country,
            streetLines: cleaned.streetLines,
            hasStreetLine: Boolean(cleaned.streetLines),
            residential: false,
            isUserTyped: false,
            company: companyCandidate
          };

          const fullAddress = results[0].formatted_address;
          const streetOnly = fullAddress.split(',')[0];
          const finalValue =
            locationFlow === 'LTL' && cleaned.hasStreetLine
              ? streetOnly
              : fullAddress;

          const displayValue = getPredictionDisplayValue(
            addressDetails.company,
            fullAddress
          );

          setInputValue(displayValue);
          setInputValueApi(finalValue);
          setAddressError('');
          setLastSelectedType('google');

          console.log('🔍 Geocoder address processed:', addressDetails);
          onSelectDetails?.(addressDetails);
          setHighlightedOption(null);
        } else {
          // If geocoding fails, use user's typed address
          handleUserTypedAddress(inputValue);
        }
      });
    };

    // --- HANDLE BLUR ---
    const handleBlur = () => {
      if (inputValue && inputValue !== defaultValue && lastSelectedType === null) {
        setAddressError('Please select an address from the suggestions');
      }
      setHighlightedOption(null);
    };

    // --- HANDLE KEY PRESS (Enter key) ---
    const handleKeyDown = event => {
      if (event.key === 'Enter') {
        if (inputValue && !highlightedOption) {
          event.preventDefault();
          setAddressError('Please select an address from the suggestions');
        }
      }
    };

    return (
      <Tooltip
        title={addressError || validattionErrors?.[errorMsg] || ''}
        arrow
        placement='top-start'
        open={Boolean(addressError || validattionErrors?.[errorMsg])}
      >
        <Autocomplete
          freeSolo
          disabled={disabled}
          clearOnEscape
          disableClearable={false}
          size='small'
          sx={{
            width: '100%',
            backgroundColor: 'white',
            '& .MuiAutocomplete-listbox': {
              padding: 0
            }
          }}
          options={options}
          getOptionLabel={option =>
            typeof option === 'string' ? option : option?.description || ''
          }
          filterOptions={x => x}
          inputValue={inputValue}
          value={
            options.find(opt =>
              !opt?.isCustomSuggestion &&
              (typeof opt === 'string'
                ? opt === inputValue
                : opt.description === inputValue)
            ) || null
          }
          onHighlightChange={(event, option) => setHighlightedOption(option)}
          onBlur={handleBlur}
          renderOption={renderOption}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'clear' || (reason === 'input' && !newInputValue)) {
              setInputValue('');
              setInputValueApi('');
              setAddressError('');
              setLastSelectedType(null);
              setHasUserTyped(false);
              onInputChange?.('');
              onSelectDetails?.(null);
              return;
            }

            if (reason === 'input') {
              setInputValue(newInputValue);
              setHasUserTyped(true);
              setAddressError('');
              setLastSelectedType(null);

              const finalValue =
                locationFlow === 'LTL'
                  ? newInputValue.split(',')[0]
                  : newInputValue;

              onInputChange?.(finalValue);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setAddressError('Please select an address from the suggestions');
              return;
            }
            if (newValue) {
              // For custom suggestions, use the exact user input
              if (newValue.isCustomSuggestion) {
                setInputValue(
                  newValue.userOriginalInput || newValue.description
                );
                setInputValueApi(
                  newValue.userOriginalInput || newValue.description
                );
                onInputChange?.(
                  newValue.userOriginalInput || newValue.description
                );
              } else {
                // For Google suggestions, use the normal flow
                const fullAddress = newValue?.description || '';
                const mainText =
                  newValue?.structured_formatting?.main_text || '';
                const descFirst = fullAddress.split(',')[0] || '';
                const termFirst = newValue?.terms?.[0]?.value || '';

                const streetOnly = mainText || descFirst || termFirst || '';
                const finalValue =
                  locationFlow === 'LTL' ? streetOnly : fullAddress;

                const companyCandidate =
                  inputType === 'quickQuote'
                    ? mainText || termFirst || descFirst
                    : null;

                const isZipOrNumeric = /^\d{3,}(?:-\d{4})?$/.test(companyCandidate?.trim());
                if (inputType === 'quickQuote' && companyCandidate && !isZipOrNumeric) {
                  const normalizeForCmp = s =>
                    s.replace(/,/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
                  const normalizedCompanyCandidate = normalizeForCmp(companyCandidate);
                  const normalizedFullAddress = normalizeForCmp(fullAddress);
                  const companyAlreadyPresent = normalizedFullAddress.startsWith(
                    normalizedCompanyCandidate
                  );

                  if (!companyAlreadyPresent) {
                    setInputValue(`${companyCandidate}, ${fullAddress}`);
                  } else {
                    setInputValue(fullAddress);
                  }
                } else {
                  setInputValue(fullAddress);
                }

                setInputValueApi(finalValue);
                onInputChange?.(finalValue);
              }

              handleSelect(newValue);
              setAddressError('');
            } else {
              setInputValue('');
              setInputValueApi('');
              onInputChange?.('');
              onSelectDetails?.(null);
              setAddressError('');
              setLastSelectedType(null);
            }
          }}
          renderInput={params => (
            <TextField
              inputRef={inputRef}
              placeholder={
                locationFlow === 'TLMultiDropCase'
                  ? 'Search Address with Google API'
                  : ''
              }
              {...params}
              error={
                Boolean(validattionErrors?.[errorMsg]) || Boolean(addressError)
              }
              size='small'
              fullWidth
              variant={inputVariant}
              label={locationFlow === 'TLMultiDropCase' ? '' : label}
              onKeyDown={handleKeyDown} // Added keydown handler
              inputProps={{
                ...params.inputProps,
                style:
                  inputVariant === 'standard'
                    ? { fontSize: 13 }
                    : locationFlow === 'TLMultiDropCase'
                    ? { fontSize: 12, height: 14 }
                    : {}
              }}
            />
          )}
        />
      </Tooltip>
    );
  }
);

export default AddressAutocomplete;
